import { ChatMessages, type RichText, useExportClient } from "@storytell/cp-client";
import { ExportFormat } from "@storytell/cp-client/src/gen/export/v3/export_service_pb";
import { subscribeToGlobalEvent } from "@storytell/events";
import { SolidStreamingMarkdown, SolidStreamingMarkdownChildren } from "@storytell/markdown";
import type { ASTNode, HeadingASTNode } from "@storytell/markdown/src/solid-markdown/toastmark/ast";
import { RichTextEditor, proseMirrorToRichText } from "@storytell/rich-text";
import type { StreamingBlock, StreamingOperation } from "@storytell/streaming";
import type { PmDoc } from "@storytell/types/richText";
import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import {
  TbAlertCircle,
  TbCheck,
  TbCopy,
  TbDownload,
  TbFileTextAi,
  TbLink,
  TbLoader2,
  TbMarkdown,
  TbPencil,
  TbX,
} from "solid-icons/tb";
import { Show, createMemo, createResource, createSignal, onCleanup, onMount } from "solid-js";
import { Dynamic, isServer } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LogoNoText } from "~/components/brand";
import { UnifiedCitationRenderer } from "~/components/chat/UnifiedCitationRenderer";
import { useAppUI } from "~/components/layouts/minimal";
import { ExtendedPaneMode } from "~/components/layouts/minimal/AppUIContext";
import { loadArtifactEdit } from "~/stores/ArtifactPersistence";
import type { KBSearchResultsCollection, WebSearchResultsCollection } from "~/utils/citationResolver";
import { buildKBCitationIndex } from "~/utils/citationResolver";
import { copyMarkdownToClipboard, copyRichTextToClipboard } from "~/utils/copyRichText";
import { copyAnchorLink, createAnchorId, generateSlug } from "~/utils/headingAnchorUtils";
import { findTitle } from "~/utils/richTextUtils";
import { buildWebSearchCollection } from "~/utils/webSearchCollectionBuilder";
import { useWire } from "~/wire";
import { TextSelectionHandler } from "../TextSelectionHandler";
import { createMarkdownStreamerFromDeltas } from "./internal/markdownStreamerFromDeltas";

interface UserFacingMessageProps {
  block: StreamingBlock;
}

export const UserFacingMessage = (props: UserFacingMessageProps) => {
  const wire = useWire();
  const operation = () => wire.stream.operation.get(props.block.operationId);

  // Find artifact block in same operation
  const artifactBlock = createMemo(() => {
    const op = operation();
    if (!op) return undefined;

    const blocks = op.blocks.map((id) => wire.stream.block.get(id)).filter((b): b is StreamingBlock => b !== undefined);

    return blocks.find(
      (b) =>
        b.messageType === ChatMessages.MessageType.ARTIFACT_CREATION ||
        b.messageType === ChatMessages.MessageType.ARTIFACT_REVISION,
    );
  });

  // Handle creating document from artifact
  const ui = useAppUI();
  const [isOpening, setIsOpening] = createSignal(false);
  const handleCreateDocument = () => {
    const artifact = artifactBlock();
    if (!artifact) {
      console.warn("No artifact block found");
      return;
    }

    setIsOpening(true);
    try {
      ui.actions.setExtendedPaneArtifact(artifact.operationId, artifact.title || "Document", artifact.blockId, true);
      ui.actions.setExtendedPaneMode(ExtendedPaneMode.ACTIVE);
    } catch (error) {
      console.error("Failed to open artifact", error);
      // TODO(amalucelli): Add toast notification for error
    } finally {
      setIsOpening(false);
    }
  };

  // Check if artifact has local edits
  const [artifactEdit, { refetch: refetchArtifactEdit }] = createResource(
    () => artifactBlock()?.blockId,
    async (blockId) => {
      // Skip IndexedDB access during SSR (not available on server)
      if (isServer || !blockId) return null;
      return await loadArtifactEdit(blockId);
    },
  );

  const hasLocalEdits = createMemo(() => {
    const edit = artifactEdit();
    if (!edit) return false;
    return JSON.stringify(edit.editedContent) !== JSON.stringify(edit.originalContent);
  });

  // Subscribe to artifact edit events to refetch edit status
  onMount(() => {
    // Force refetch on client after hydration to load from IndexedDB
    if (!isServer && artifactBlock()?.blockId) {
      refetchArtifactEdit();
    }

    const unsubscribeCleared = subscribeToGlobalEvent("artifact-edit-cleared", (event) => {
      const currentBlockId = artifactBlock()?.blockId;
      if (currentBlockId && event.detail.blockId === currentBlockId) {
        refetchArtifactEdit();
      }
    });

    const unsubscribeSaved = subscribeToGlobalEvent("artifact-edit-saved", (event) => {
      const currentBlockId = artifactBlock()?.blockId;
      if (currentBlockId && event.detail.blockId === currentBlockId) {
        refetchArtifactEdit();
      }
    });

    onCleanup(() => {
      unsubscribeCleared();
      unsubscribeSaved();
    });
  });

  // Build web search results collection from all webSearchResults objects in the same message
  const webSearchResults = createMemo<WebSearchResultsCollection | undefined>(() => {
    const op = operation();
    if (!op) return undefined;

    const messageId = op.messageId;
    if (!messageId) return undefined;

    const message = wire.stream.message.get(messageId);
    if (!message) return undefined;

    const allWebSearchResults: ChatMessages.WebSearchResult[] = [];

    // Look through all operations in this message
    for (const operationId of message.operations ?? []) {
      const messageOp = wire.stream.operation.get(operationId);
      if (!messageOp) continue;

      // Look through all blocks in this operation
      for (const blockId of messageOp.blocks ?? []) {
        const block = wire.stream.block.get(blockId);
        if (block?.webSearchResults) {
          // Collect individual webSearchResults objects from each block
          allWebSearchResults.push(block.webSearchResults);
        }
      }
    }

    // Build collection for efficient lookup by tool ID
    return allWebSearchResults.length > 0 ? buildWebSearchCollection(allWebSearchResults) : undefined;
  });

  // Build KB search results collection from all searchResults objects in the same message
  const kbSearchResults = createMemo<KBSearchResultsCollection | undefined>(() => {
    const op = operation();
    if (!op) return undefined;

    const messageId = op.messageId;
    if (!messageId) return undefined;

    const message = wire.stream.message.get(messageId);
    if (!message) return undefined;

    const collections: KBSearchResultsCollection[] = [];

    for (const operationId of message.operations ?? []) {
      const messageOp = wire.stream.operation.get(operationId);
      if (!messageOp) continue;
      if (messageOp.activity !== "user_kb_search_v1" && messageOp.activity !== "kb_search") continue;

      for (const blockId of messageOp.blocks ?? []) {
        const block = wire.stream.block.get(blockId);
        if (block?.searchResults) {
          const collection = buildKBCitationIndex(block.searchResults);
          collections.push(collection);
        }
      }
    }

    if (collections.length === 0) return undefined;

    const merged: KBSearchResultsCollection = {
      byToolId: new Map(),
      byChunkId: new Map(),
    };

    for (const collection of collections) {
      for (const [toolId, results] of collection.byToolId) {
        merged.byToolId.set(toolId, results);
      }
      for (const [chunkId, chunkData] of collection.byChunkId) {
        merged.byChunkId.set(chunkId, chunkData);
      }
    }

    return merged;
  });

  const hideContentOnComplete = () => {
    const op = operation();
    if (!op) return false;
    const activity = op.activity;
    if ((activity === "user_kb_search_v1" || activity === "kb_search") && op.isComplete) {
      return true;
    }
    return false;
  };

  const hasContent = () => !!props.block.deltas.length && !hideContentOnComplete();

  const getText = () => props.block.deltas.map((delta) => delta.text).join("");
  const [hovered, setHovered] = createSignal(false);

  // Track heading IDs to handle duplicates (non-reactive ref)
  const usedHeadingIds: Set<string> = new Set();

  // Extract text content from AST node recursively
  const extractTextFromNode = (node: ASTNode): string => {
    if (node.literal) return node.literal;
    if (node.children && node.children.length > 0) {
      return node.children.map((child) => extractTextFromNode(child)).join("");
    }
    return "";
  };

  // Custom heading component with anchor support
  const AnchorHeading = (headingProps: { node: HeadingASTNode }) => {
    const level = () => headingProps.node.level;
    const headingText = () => extractTextFromNode(headingProps.node);

    // Only add anchors for h1, h2, h3
    const shouldAddAnchor = () => level() <= 3;

    // Generate anchor ID (non-reactive, computed once)
    const anchorId = createMemo(() => {
      if (!shouldAddAnchor()) return undefined;

      const messageId = props.block.messageId;
      if (!messageId) return undefined;

      const text = headingText();
      if (!text) return undefined;

      // Generate unique ID and track it (no reactive update)
      const id = createAnchorId(messageId, text, usedHeadingIds);
      usedHeadingIds.add(id);
      return id;
    });

    const [hoveredHeading, setHoveredHeading] = createSignal(false);
    const [linkCopied, setLinkCopied] = createSignal(false);

    const handleCopyLink = async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const id = anchorId();
      if (id) {
        await copyAnchorLink(id);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    };

    const headingClass = () => {
      switch (level()) {
        case 1:
          return "font-medium text-2xl mb-3 mt-4";
        case 2:
          return "font-medium text-xl mb-3 mt-4";
        case 3:
          return "font-medium text-lg mb-3 mt-4";
        case 4:
          return "font-medium text-base mb-3 mt-4";
        case 5:
          return "font-medium text-base mb-3 mt-4";
        case 6:
          return "font-medium text-sm mb-3 mt-4";
        default:
          return "";
      }
    };

    return (
      <Dynamic
        component={`h${level()}`}
        id={anchorId()}
        class={twMerge(headingClass(), "group relative")}
        onMouseEnter={() => setHoveredHeading(true)}
        onMouseLeave={() => setHoveredHeading(false)}
      >
        <SolidStreamingMarkdownChildren node={headingProps.node} />
        <Show when={shouldAddAnchor() && anchorId()}>
          <button
            type="button"
            onClick={handleCopyLink}
            class={twMerge(
              "inline-flex items-center justify-center ml-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-all",
              linkCopied()
                ? "text-green-600 dark:text-green-400"
                : "text-muted-text hover:text-primary-text hover:bg-inset",
            )}
            title={linkCopied() ? "Link copied!" : "Copy link to heading"}
            aria-label={linkCopied() ? "Link copied!" : "Copy link to heading"}
          >
            <Show when={linkCopied()} fallback={<TbLink size={16} />}>
              <TbCheck size={16} />
            </Show>
          </button>
        </Show>
      </Dynamic>
    );
  };

  // Check if this answer's artifact is currently open in ExtendedPane
  const isArtifactOpen = () => {
    const artifact = artifactBlock();
    if (!artifact) return false;

    // Check if ExtendedPane is visible
    const paneIsActive =
      ui.state.extendedPane.mode === ExtendedPaneMode.ACTIVE || ui.state.extendedPane.mode === ExtendedPaneMode.PREVIEW;
    if (!paneIsActive) return false;

    // Match by either blockId or operationId
    return (
      ui.state.extendedPane.activeArtifact?.blockId === artifact.blockId ||
      ui.state.extendedPane.activeArtifact?.operationId === artifact.operationId
    );
  };
  const [copied, setCopied] = createSignal(false);
  const [markdownCopied, setMarkdownCopied] = createSignal(false);
  const [download, setDownload] = createSignal({
    downloading: false,
    downloaded: false,
    err: false,
  });

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();

    const content = contentRef();
    if (!content) {
      try {
        await navigator.clipboard.writeText(getText());
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
      return;
    }

    const success = await copyRichTextToClipboard(content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } else {
      try {
        await navigator.clipboard.writeText(getText());
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      } catch (fallbackErr) {
        console.error("Fallback copy also failed:", fallbackErr);
      }
    }
  };

  const handleMarkdownCopy = async (e: MouseEvent) => {
    e.stopPropagation();

    const success = await copyMarkdownToClipboard(getText());
    if (success) {
      setMarkdownCopied(true);
      setTimeout(() => setMarkdownCopied(false), 1000);
    }
  };

  const downloadArtifactAsPdf = async (
    richText: RichText.RichText,
    title: string,
    authToken: () => string | undefined,
  ) => {
    if (isServer) return;

    const result = await useExportClient({ authToken }).exportRichText({
      richText: richText,
      filename: title,
      format: ExportFormat.PDF,
    });

    if (result.isErr()) {
      console.error("Failed to download artifact:", result.error);
      throw result.error;
    }

    try {
      const blob = new Blob([result.value.fileData], { type: result.value.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9\s'":-]/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download artifact:", err);
    }
  };

  const handleDownload = async (e: MouseEvent) => {
    e.stopPropagation();

    const artifact = artifactBlock();
    if (!artifact) return;

    setDownload({ downloading: true, downloaded: false, err: false });

    try {
      let richText: RichText.RichText;
      if (artifact.finalContent?.type === "richText" && artifact.finalContent.richText) {
        richText = proseMirrorToRichText(artifact.finalContent.richText);
      } else {
        const markdown = getText();
        richText = proseMirrorToRichText({
          type: "doc",
          content: [{ type: "paragraph", content: [{ type: "text", text: markdown }] }],
        } as unknown as PmDoc);
      }

      let title = findTitle(richText) || artifact.title;
      if (typeof title !== "string") {
        title = "Generated Document";
      }

      await downloadArtifactAsPdf(richText, title, wire.auth.token);

      setDownload({ downloading: false, downloaded: true, err: false });
      setTimeout(() => setDownload((prev) => ({ ...prev, downloaded: false })), 2000);
    } catch (err) {
      console.error("Failed to download:", err);
      setDownload({ downloading: false, downloaded: false, err: true });
    }
  };

  // Create streaming markdown instance
  const markdownStreamer = createMarkdownStreamerFromDeltas(() => props.block.deltas.map((delta) => delta.text));

  // Ref for text selection
  const [contentRef, setContentRef] = createSignal<HTMLDivElement>();

  return (
    <Show when={props.block} fallback={<div class="text-sm text-gray-700 dark:text-gray-300">Loading...</div>}>
      <Show when={hasContent()}>
        <>
          {/* Show minimized state when artifact is open */}
          <Show when={isArtifactOpen()}>
            <div class="flex items-center gap-2 p-3 bg-panel/50 rounded-lg border border-muted-border/50">
              <TbFileTextAi size={16} class="text-muted-text flex-shrink-0" />
              <div class="flex items-center gap-2 flex-1">
                <span class="text-sm text-muted-text">Viewing in Editor</span>
                <Show when={hasLocalEdits()}>
                  <span class="text-xs text-blue-600 dark:text-blue-400">(Modified)</span>
                </Show>
              </div>
              <button
                type="button"
                onClick={() => ui.actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN)}
                class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-tile hover:bg-tile-hover border border-muted-border text-primary-text transition-colors shadow-md"
              >
                <TbX size={14} />
                <span>Close Editor</span>
              </button>
            </div>
          </Show>

          <Show when={!isArtifactOpen()}>
            <div
              ref={setContentRef}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              data-renderer="user-facing-message"
              class="text-sm text-primary-text relative"
            >
              <div class="text-base max-w-none">
                <Show
                  when={
                    props.block.isComplete && props.block.finalContent?.type === "richText" && props.block.finalContent
                  }
                  fallback={
                    <SolidStreamingMarkdown
                      doc={markdownStreamer.doc}
                      options={{
                        components: {
                          heading: AnchorHeading,
                          htmlInline: (props) => {
                            const literal = () => props.node.literal;
                            const isCitation = () => literal()?.startsWith("<cite>") && literal()?.endsWith("</cite>");
                            const citationValue = () => {
                              const lit = literal();
                              if (!lit) return "";
                              return lit.replace("<cite>", "").replace("</cite>", "");
                            };

                            return (
                              <Show when={isCitation()} fallback={null}>
                                <UnifiedCitationRenderer
                                  node={{ attrs: { value: citationValue() } }}
                                  selected={false}
                                  webSearchResults={webSearchResults()}
                                  kbSearchResults={kbSearchResults()}
                                />
                              </Show>
                            );
                          },
                        },
                        classes: {
                          document: "wrap-break-word whitespace-break-spaces",
                          heading: (node) => {
                            switch (node.level) {
                              case 1:
                                return "font-medium text-2xl mb-3 mt-4";
                              case 2:
                                return "font-medium text-xl mb-3 mt-4";
                              case 3:
                                return "font-medium text-lg mb-3 mt-4";
                              case 4:
                                return "font-medium text-base mb-3 mt-4";
                              case 5:
                                return "font-medium text-base mb-3 mt-4";
                              case 6:
                                return "font-medium text-sm mb-3 mt-4";
                              default:
                                return "";
                            }
                          },
                          // paragraph: "mb-2",
                          list: (node) => {
                            switch (node.listData?.type) {
                              case "ordered":
                                return "list-decimal pl-4";
                              case "bullet":
                                return "list-disc pl-4";
                              default:
                                return "";
                            }
                          },
                          thematicBreak: "border-gray-300 dark:border-gray-700 my-4",
                          paragraph: "mb-4",
                          item: "mb-2",
                          code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
                          blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
                          tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border mb-4",
                          codeBlock: "bg-inset px-2 py-2 mb-2 rounded text-sm overflow-x-auto",
                          table: "",
                          tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
                          tableCell: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
                          tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
                          tableBody: "bg-tile",
                          link: "text-brand underline",
                        },
                      }}
                    />
                  }
                >
                  {(finalContent) => (
                    <RichTextEditor
                      doc={finalContent().richText}
                      citationRenderer={(citationProps) => (
                        <UnifiedCitationRenderer
                          node={citationProps.node}
                          selected={citationProps.selected}
                          webSearchResults={webSearchResults()}
                          kbSearchResults={kbSearchResults()}
                        />
                      )}
                    />
                  )}
                </Show>
              </div>

              <Show when={!props.block.isComplete}>
                <div class="inline-block w-2 h-4 bg-blue-500 animate-pulse ml-1" />
              </Show>

              {/* Action buttons - Always visible at bottom when complete */}
              <Show when={props.block.isComplete}>
                <div class="flex flex-row items-center gap-2 mt-3">
                  {/* Copy button */}
                  <StTooltip content={copied() ? "Copied!" : "Copy"}>
                    <StTooltipTrigger
                      as="button"
                      type="button"
                      class="group/btn shadow-md cursor-pointer rounded-lg bg-tile hover:bg-tile-hover border border-muted-border transition-all p-1.5"
                      onClick={handleCopy}
                    >
                      <Dynamic
                        component={copied() ? TbCheck : TbCopy}
                        size={16}
                        class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                      />
                    </StTooltipTrigger>
                  </StTooltip>

                  {/* Markdown Copy button */}
                  <StTooltip content={markdownCopied() ? "Copied!" : "Copy as Markdown"}>
                    <StTooltipTrigger
                      as="button"
                      type="button"
                      class="group/btn shadow-md cursor-pointer rounded-lg bg-tile hover:bg-tile-hover border border-muted-border transition-all p-1.5"
                      onClick={handleMarkdownCopy}
                    >
                      <Dynamic
                        component={markdownCopied() ? TbCheck : TbMarkdown}
                        size={16}
                        class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                      />
                    </StTooltipTrigger>
                  </StTooltip>

                  {/* Download button - only show when artifact exists */}
                  <Show when={artifactBlock()}>
                    <StTooltip
                      content={
                        download().err
                          ? "Click to try again"
                          : download().downloaded
                            ? "Downloaded!"
                            : download().downloading
                              ? "Downloading..."
                              : "Download PDF"
                      }
                    >
                      <StTooltipTrigger
                        as="button"
                        type="button"
                        class="group/btn shadow-md cursor-pointer rounded-lg bg-tile hover:bg-tile-hover border border-muted-border transition-all p-1.5"
                        classList={{
                          "text-red-500": download().err,
                        }}
                        onClick={handleDownload}
                        disabled={download().downloading}
                      >
                        <Dynamic
                          component={
                            download().err
                              ? TbAlertCircle
                              : download().downloading
                                ? TbLoader2
                                : download().downloaded
                                  ? TbCheck
                                  : TbDownload
                          }
                          size={16}
                          class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                          classList={{
                            "animate-spin": download().downloading,
                          }}
                        />
                      </StTooltipTrigger>
                    </StTooltip>
                  </Show>

                  {/* Open Editor button */}
                  <Show when={artifactBlock() && !isArtifactOpen()}>
                    <StTooltip
                      content={
                        isOpening() ? "Opening..." : hasLocalEdits() ? "Continue Editing (Modified)" : "Open Editor"
                      }
                    >
                      <div class="relative inline-flex">
                        <StTooltipTrigger
                          as="button"
                          type="button"
                          onClick={handleCreateDocument}
                          disabled={isOpening()}
                          class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs shadow-md rounded-lg bg-tile hover:bg-tile-hover border border-muted-border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <TbFileTextAi size={16} class="flex-shrink-0" />
                          <span>
                            {isOpening() ? "Opening..." : hasLocalEdits() ? "Continue Editing" : "Open Editor"}
                          </span>
                        </StTooltipTrigger>
                        <Show when={hasLocalEdits()}>
                          <div class="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-panel" />
                        </Show>
                      </div>
                    </StTooltip>
                  </Show>
                </div>
              </Show>
            </div>
          </Show>

          {/* Text selection handler */}
          <TextSelectionHandler ref={contentRef} isReadOnly={false} />
        </>
      </Show>
    </Show>
  );
};
